import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { AIAssistantProvider } from './contexts/AIAssistantContext';
import Layout from './components/Layout/Layout';
import HomePage from './pages/HomePage';
import OnboardingPage from './pages/OnboardingPage';
import ProjectCreationPage from './pages/ProjectCreationPage';
import ProjectDashboardPage from './pages/ProjectDashboardPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ConfirmRegistrationPage from './pages/ConfirmRegistrationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BuilderDashboardPageNew from './pages/BuilderDashboardPageNew';
import BuilderProjectViewPage from './pages/BuilderProjectViewPage';
import ProfilePage from './pages/ProfilePage';
import SoWReviewPage from './pages/SoWReviewPage';
import QuoteSubmissionPage from './pages/QuoteSubmissionPage';
import QuoteDetailsPage from './pages/QuoteDetailsPage';
import BuilderProfilePage from './pages/BuilderProfilePage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import SmartDashboard from './components/SmartDashboard';
import AIAssistantChat from './components/AIAssistant/AIAssistantChat';
import SessionManager from './components/Auth/SessionManager';

// Initialize Amplify configuration
import './config/amplify';

function App() {
  return (
    <AuthProvider>
      {/* SessionManager must be inside AuthProvider and Router (Router is in index.tsx) */}
      <SessionManager />
      
      <AIAssistantProvider>
        <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh',
          
          // --- ARCHITECTURAL BLUEPRINT BACKGROUND ---
          backgroundColor: '#f0f4f8',
          position: 'relative',
          
          // Main blueprint grid
          backgroundImage: `
            linear-gradient(rgba(100, 149, 237, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100, 149, 237, 0.08) 1px, transparent 1px),
            linear-gradient(rgba(100, 149, 237, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100, 149, 237, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px, 100px 100px, 20px 20px, 20px 20px',
          backgroundPosition: '-1px -1px, -1px -1px, -1px -1px, -1px -1px',
          
          // Blueprint corner marks (like architectural drawings)
          '&::before': {
            content: '""',
            position: 'fixed',
            top: '20px',
            left: '20px',
            width: '60px',
            height: '60px',
            borderTop: '2px solid rgba(100, 149, 237, 0.15)',
            borderLeft: '2px solid rgba(100, 149, 237, 0.15)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          
          '&::after': {
            content: '""',
            position: 'fixed',
            top: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderTop: '2px solid rgba(100, 149, 237, 0.15)',
            borderRight: '2px solid rgba(100, 149, 237, 0.15)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          
          // Bottom corner marks
          '& .blueprint-corner-bl': {
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            width: '60px',
            height: '60px',
            borderBottom: '2px solid rgba(100, 149, 237, 0.15)',
            borderLeft: '2px solid rgba(100, 149, 237, 0.15)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          
          '& .blueprint-corner-br': {
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderBottom: '2px solid rgba(100, 149, 237, 0.15)',
            borderRight: '2px solid rgba(100, 149, 237, 0.15)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          
          // Subtle measurement marks along edges
          '& .blueprint-ruler-top': {
            position: 'fixed',
            top: '0',
            left: '100px',
            right: '100px',
            height: '10px',
            backgroundImage: 'repeating-linear-gradient(90deg, rgba(100, 149, 237, 0.12) 0px, rgba(100, 149, 237, 0.12) 1px, transparent 1px, transparent 50px)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          
          '& .blueprint-ruler-left': {
            position: 'fixed',
            left: '0',
            top: '100px',
            bottom: '100px',
            width: '10px',
            backgroundImage: 'repeating-linear-gradient(0deg, rgba(100, 149, 237, 0.12) 0px, rgba(100, 149, 237, 0.12) 1px, transparent 1px, transparent 50px)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          
          // Ensure content stays above background
          '& > *:not([class*="blueprint"])': {
            position: 'relative',
            zIndex: 1,
          },
        }}
        >
          {/* Skip to main content link for accessibility */}
          <a href="#main-content" className="skip-link">
            Skip to main content
          </a>
          
          <Routes>
            {/* Redirect root to app */}
            <Route path="/" element={<Navigate to="/app" replace />} />
            <Route path="/projects/types" element={<Navigate to="/app" replace />} />
            
            {/* Authentication routes (no layout) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/confirm-registration" element={<ConfirmRegistrationPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Application routes (with layout) */}
            <Route path="/app" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route 
                path="projects/create" 
                element={
                  <ProtectedRoute requiredRole="homeowner">
                    <ProjectCreationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="projects" 
                element={
                  <ProtectedRoute requiredRole="homeowner">
                    <ProjectDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="projects/:projectId" 
                element={
                  <ProtectedRoute requiredRole="homeowner">
                    <ProjectDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="dashboard" 
                element={
                  <ProtectedRoute requiredRole="homeowner">
                    <SmartDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Builder Routes */}
              <Route 
                path="builder/dashboard" 
                element={
                  <ProtectedRoute requiredRole="builder">
                    <BuilderDashboardPageNew />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="builder/projects/:projectId" 
                element={
                  <ProtectedRoute requiredRole="builder">
                    <BuilderProjectViewPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="builder/projects/:projectId/quote" 
                element={
                  <ProtectedRoute requiredRole="builder">
                    <BuilderProjectViewPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Legacy Builder Routes */}
              <Route 
                path="builder/sow/:sowId" 
                element={
                  <ProtectedRoute>
                    <SoWReviewPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="builder/quote/:sowId" 
                element={
                  <ProtectedRoute>
                    <QuoteSubmissionPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="builder/quotes/:quoteId" 
                element={
                  <ProtectedRoute>
                    <QuoteDetailsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="builder/quotes/:quoteId/edit" 
                element={
                  <ProtectedRoute>
                    <QuoteSubmissionPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="builder/profile" 
                element={
                  <ProtectedRoute requiredRole="builder">
                    <BuilderProfilePage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Redirect unknown app routes to app home */}
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Route>
            
            {/* Legacy routes - redirect to new structure */}
            <Route path="/onboarding" element={<Navigate to="/app/onboarding" replace />} />
            <Route path="/projects/*" element={<Navigate to="/app/projects/create" replace />} />
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/builder/*" element={<Navigate to="/app/builder/dashboard" replace />} />
            <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
            
            {/* Catch all - redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <AIAssistantChat />
        </Box>
      </AIAssistantProvider>
    </AuthProvider>
  );
}

export default App;